import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, Tooltip, Legend, Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend, Filler);

const PERIODS = [
  { key: 'today', label: 'Bugün' },
  { key: 'week', label: 'Bu Hafta' },
  { key: 'month', label: 'Bu Ay' }
];

const formatMoney = (n) => `₺${Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
const formatDayLabel = (isoDate) => {
  const [, m, d] = isoDate.split('-');
  return `${d}.${m}`;
};

// Tenant tarafinda (client/src/pages/Reports.js, /api/reports) ve yonetim
// panelinde (firma firma detay, /api/admin/branches/:branchId/reports) AYNI
// bilesen kullaniliyor - apiBase/token opsiyonel: verilmezse tenant'in kendi
// crm_token'i ve /api/reports'u kullanilir (AdminAICenterPanel'deki CallOps
// sekmeleriyle ayni geriye-uyumlu prop deseni).
const ReportsView = ({ apiBase = '/api/reports', token, title }) => {
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const authToken = token || localStorage.getItem('crm_token');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(apiBase, {
        params: { period },
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setData(data);
    } catch (err) { console.error('Fetch report error:', err); }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, [apiBase, period]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const cards = data ? [
    { label: 'Gelen Mesaj', value: data.summary.messages_received, icon: '💬', color: '#00f2fe' },
    { label: 'Giden Mesaj', value: data.summary.messages_sent, icon: '📤', color: '#60a5fa' },
    { label: 'Yeni Müşteri', value: data.summary.new_customers, icon: '👤', color: '#8b5cf6' },
    { label: 'Oluşturulan Randevu', value: data.summary.appointments_created, icon: '📅', color: '#fbbf24' },
    { label: 'Tamamlanan Randevu', value: data.summary.appointments_completed, icon: '✅', color: '#10b981' },
    { label: 'İptal / Gelmedi', value: data.summary.appointments_cancelled, icon: '⛔', color: '#f43f5e' },
    { label: 'Ciro', value: formatMoney(data.summary.revenue), icon: '💰', color: '#34d399' },
    { label: 'Kampanya Mesajı', value: data.summary.campaign_messages_sent, icon: '📣', color: '#ec4899' }
  ] : [];

  const chartData = data ? {
    labels: data.trend.dates.map(formatDayLabel),
    datasets: [
      {
        label: 'Gelen Mesaj',
        data: data.trend.messages_received,
        borderColor: '#00f2fe',
        backgroundColor: 'rgba(0,242,254,0.12)',
        fill: true,
        tension: 0.3,
        pointRadius: 2
      },
      {
        label: 'Randevu',
        data: data.trend.appointments_created,
        borderColor: '#fbbf24',
        backgroundColor: 'rgba(251,191,36,0.08)',
        fill: true,
        tension: 0.3,
        pointRadius: 2
      }
    ]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, labels: { color: '#9ca3af', font: { size: 12 } } },
      tooltip: { enabled: true }
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#9ca3af', font: { size: 11 } } },
      y: { grid: { color: 'rgba(255,255,255,0.03)' }, ticks: { color: '#9ca3af', font: { size: 11 }, precision: 0 } }
    }
  };

  return (
    <div className="rv-root">
      <div className="rv-header">
        {title && <h2 className="rv-title">{title}</h2>}
        <div className="rv-period-tabs">
          {PERIODS.map(p => (
            <button key={p.key} className={`rv-period-tab ${period === p.key ? 'active' : ''}`} onClick={() => setPeriod(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading || !data ? (
        <div className="loading"><div className="loading-spinner"></div></div>
      ) : (
        <>
          <div className="rv-kpi-grid">
            {cards.map(c => (
              <div key={c.label} className="rv-kpi-card">
                <div className="rv-kpi-left">
                  <span className="rv-kpi-label">{c.label}</span>
                  <span className="rv-kpi-value">{c.value}</span>
                </div>
                <div className="rv-kpi-icon" style={{ background: `${c.color}22`, color: c.color }}>{c.icon}</div>
              </div>
            ))}
          </div>

          <div className="rv-chart-card">
            <div className="rv-chart-title">Son {data.trend.dates.length} Gün</div>
            <div className="rv-chart-container">
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>
        </>
      )}

      <style>{`
        .rv-header { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
        .rv-title { font-size: 18px; font-weight: 700; color: #fff; margin: 0; }
        .rv-period-tabs { display: flex; gap: 4px; background: rgba(255,255,255,0.04); padding: 4px; border-radius: 10px; }
        .rv-period-tab { padding: 7px 16px; border: none; border-radius: 8px; background: transparent; color: #9ca3af; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.15s; }
        .rv-period-tab:hover { color: #e5e7eb; }
        .rv-period-tab.active { background: linear-gradient(135deg, #00c2fe, #3b82f6); color: #fff; }

        .rv-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
        @media (max-width: 1100px) { .rv-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 560px) { .rv-kpi-grid { grid-template-columns: 1fr; } }

        .rv-kpi-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          padding: 18px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          transition: all 0.2s;
        }
        .rv-kpi-card:hover { border-color: rgba(255,255,255,0.12); background: rgba(255,255,255,0.05); }
        .rv-kpi-left { display: flex; flex-direction: column; }
        .rv-kpi-label { font-size: 12px; color: #9ca3af; margin-bottom: 6px; }
        .rv-kpi-value { font-size: 22px; font-weight: 700; color: #fff; }
        .rv-kpi-icon { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0; }

        .rv-chart-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); border-radius: 16px; padding: 20px; }
        .rv-chart-title { font-size: 13px; font-weight: 600; color: #9ca3af; margin-bottom: 14px; }
        .rv-chart-container { height: 260px; }
      `}</style>
    </div>
  );
};

export default ReportsView;
